import { useEffect } from 'react';
import { toast } from 'sonner';

// Trascinamento di file e CARTELLE su tutte le zone di caricamento del CRM, senza toccare le
// singole pagine: al rilascio si cerca il campo <input type="file"> della zona (dentro la stessa
// <label>, o il più vicino risalendo la pagina, purché unico), gli si consegnano i file come se
// fossero stati scelti dalla finestra di sistema e si genera l'evento "change" — così parte la
// stessa logica di caricamento della pagina. Le cartelle vengono lette per intero (sottocartelle
// comprese); si rispettano i formati accettati dal campo e "multiple". Fuori da una zona di
// caricamento il rilascio viene ignorato (il browser altrimenti aprirebbe il file al posto del CRM).

const CLASSE_EVIDENZA = 'jv-trascina-attivo';
const RISALITA_MAX = 6;

const campoPerElemento = (el) => {
  if (!el || !el.closest) return null;
  const label = el.closest('label');
  const inLabel = label && label.querySelector('input[type="file"]:not([disabled])');
  if (inLabel) return { input: inLabel, zona: label };
  let nodo = el;
  for (let i = 0; i < RISALITA_MAX && nodo && nodo !== document.body; i += 1) {
    const campi = nodo.querySelectorAll ? nodo.querySelectorAll('input[type="file"]:not([disabled])') : [];
    if (campi.length === 1) return { input: campi[0], zona: nodo };
    if (campi.length > 1) return null; // zona ambigua: più campi di caricamento insieme
    nodo = nodo.parentElement;
  }
  return null;
};

const accetta = (input, file) => {
  const regole = (input.getAttribute('accept') || '').split(',').map((r) => r.trim().toLowerCase()).filter(Boolean);
  if (!regole.length) return true;
  const nome = file.name.toLowerCase();
  const tipo = (file.type || '').toLowerCase();
  return regole.some((r) => (r.startsWith('.') ? nome.endsWith(r)
    : r.endsWith('/*') ? tipo.startsWith(r.slice(0, -1))
      : tipo === r || (r === 'image/jpg' && tipo === 'image/jpeg')));
};

// file di una voce trascinata: se è una cartella, tutti i file al suo interno
const leggiVoce = (voce) => new Promise((resolve) => {
  if (!voce) { resolve([]); return; }
  if (voce.isFile) { voce.file((f) => resolve([f]), () => resolve([])); return; }
  if (!voce.isDirectory) { resolve([]); return; }
  const lettore = voce.createReader();
  const tutte = [];
  const leggiBlocco = () => lettore.readEntries(async (blocco) => {
    if (!blocco.length) {
      const annidati = await Promise.all(tutte.map(leggiVoce));
      resolve(annidati.flat());
      return;
    }
    tutte.push(...blocco);
    leggiBlocco(); // readEntries restituisce al massimo ~100 voci per volta
  }, () => resolve([]));
  leggiBlocco();
});

const fileTrascinati = async (dt) => {
  const voci = Array.from(dt.items || [])
    .filter((it) => it.kind === 'file')
    .map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null));
  if (voci.length && voci.every(Boolean)) {
    const gruppi = await Promise.all(voci.map(leggiVoce));
    return gruppi.flat();
  }
  return Array.from(dt.files || []);
};

const haFile = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

export default function TrascinaFile() {
  useEffect(() => {
    let zonaAttiva = null;
    const evidenzia = (zona) => {
      if (zonaAttiva === zona) return;
      if (zonaAttiva) zonaAttiva.classList.remove(CLASSE_EVIDENZA);
      zonaAttiva = zona;
      if (zona) zona.classList.add(CLASSE_EVIDENZA);
    };

    const sopra = (e) => {
      if (!haFile(e)) return;
      e.preventDefault(); // necessario per poter rilasciare
      const campo = campoPerElemento(e.target);
      e.dataTransfer.dropEffect = campo ? 'copy' : 'none';
      evidenzia(campo ? campo.zona : null);
    };

    const fuori = (e) => {
      if (!e.relatedTarget) evidenzia(null); // uscito dalla finestra
    };

    const rilascio = async (e) => {
      if (!haFile(e)) return;
      e.preventDefault();
      evidenzia(null);
      const campo = campoPerElemento(e.target);
      if (!campo) return;
      const { input } = campo;
      const tutti = await fileTrascinati(e.dataTransfer);
      const validi = tutti.filter((f) => accetta(input, f));
      if (!validi.length) {
        toast.error('Formato non accettato in questo campo');
        return;
      }
      const scelti = input.multiple ? validi : validi.slice(0, 1);
      if (!input.multiple && validi.length > 1) toast.info('Questo campo accetta un solo file: caricato il primo');
      if (validi.length < tutti.length) toast.info(`${tutti.length - validi.length} file ignorati (formato non accettato)`);
      const dt = new DataTransfer();
      scelti.forEach((f) => dt.items.add(f));
      input.files = dt.files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    document.addEventListener('dragover', sopra);
    document.addEventListener('dragleave', fuori);
    document.addEventListener('drop', rilascio);
    return () => {
      document.removeEventListener('dragover', sopra);
      document.removeEventListener('dragleave', fuori);
      document.removeEventListener('drop', rilascio);
    };
  }, []);
  return null;
}
